import { useCallback, useEffect, useState } from 'react'

import { api } from '../../lib/api'
import { DatePicker } from '../../lib/DatePicker'
import { Pager } from '../../lib/Pager'
import { SortTh, sortParam, useSort } from '../../lib/Sort'
import { useAuthedSession } from '../../lib/SessionContext'

/* ============================================================================
   ข้อมูลหลัก → วันหยุด · backend: src/routes/adminHolidays.js

   ไม่ได้ใช้ MasterTable เหมือนอีก 4 หน้า เพราะข้อมูลเป็น **สองชั้น**:
   ปฏิทินหนึ่งใบ (CalendarMaster) มีวันหยุดหลายวัน (CalendarDetail)
   ตารางหลักจึงเป็นรายการปฏิทิน แล้วกดเข้าไปดู/แก้วันหยุดในแผงด้านข้าง

   ⚠️ ปีหนึ่งมีได้หลายปฏิทิน — ของจริงปี 2023 มี 3 ใบ (พนักงานทั่วไป, ศูนย์ USO,
      แม่บ้าน) คนละกลุ่มหยุดคนละวัน อย่าออกแบบโดยคิดว่าปีละใบ
   ============================================================================ */

const BASE = '/api/admin/holidays'
const nf = new Intl.NumberFormat('th-TH')

type Calendar = {
  CalendarID: number
  CalendarYear: number
  CalendarRemark: string
  CompanyCode: string
  dayCount: number
}

type Day = {
  DateID: number
  CalendarID: number
  HolidayDate: string
  HolidayDesc: string
}

/** YYYY-MM-DD จากค่าที่ API ส่งมา (date ของ mssql มาเป็น ISO เต็ม) */
const asDate = (v: string) => String(v).slice(0, 10)

export default function Holidays() {
  const session = useAuthedSession()
  const mayWrite =
    session.kind === 'admin' && (session.role === 'superadmin' || session.role === 'admin')

  const [rows, setRows] = useState<Calendar[] | null>(null)
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const sort = useSort([], () => setPage(1))

  const [companyCode, setCompanyCode] = useState('')
  const [companies, setCompanies] = useState<{ code: string; name: string | null }[]>([])

  /** null = ปิด · 'new' = เพิ่มปฏิทิน · ตัวเลข = เปิดวันหยุดของปฏิทินนั้น */
  const [open, setOpen] = useState<number | 'new' | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const qs = new URLSearchParams({ page: String(page), pageSize: String(pageSize) })
      const sp = sortParam(sort.sort)
      if (sp) qs.set('sort', sp)
      if (companyCode) qs.set('companyCode', companyCode)

      const d = await api<{ rows: Calendar[]; total: number }>(`${BASE}?${qs}`)
      setRows(d.rows)
      setTotal(d.total)
      setError(null)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [page, pageSize, sort.sort, companyCode])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    let alive = true
    api<{ options: { code: string; name: string | null }[] }>('/api/admin/companies/options')
      .then((d) => alive && setCompanies(d.options))
      .catch(() => {})
    return () => {
      alive = false
    }
  }, [])

  return (
    <>
      <h1>วันหยุด</h1>

      {error ? <p className="error">{error}</p> : null}
      {!rows && !error ? <p>กำลังโหลด…</p> : null}

      <div className="emp-toolbar">
        {companies.length > 1 ? (
          <select
            value={companyCode}
            onChange={(e) => {
              setCompanyCode(e.target.value)
              setPage(1)
            }}
            aria-label="บริษัท"
          >
            <option value="">ทุกบริษัท</option>
            {companies.map((c) => (
              <option key={c.code} value={c.code}>
                {c.name ? `${c.code} — ${c.name}` : c.code}
              </option>
            ))}
          </select>
        ) : null}

        {mayWrite ? (
          <button type="button" onClick={() => setOpen('new')}>
            เพิ่มปฏิทิน
          </button>
        ) : null}
      </div>

      {rows ? (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <SortTh label="ปี" sortKey="year" sort={sort.sort} onToggle={sort.toggle} className="num" />
                <SortTh label="ชื่อปฏิทิน" sortKey="remark" sort={sort.sort} onToggle={sort.toggle} />
                <SortTh label="บริษัท" sortKey="company" sort={sort.sort} onToggle={sort.toggle} />
                <SortTh label="วันหยุด" sortKey="days" sort={sort.sort} onToggle={sort.toggle} className="num" />
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.CalendarID}>
                  <td className="num">{r.CalendarYear}</td>
                  <td>{r.CalendarRemark}</td>
                  <td>{r.CompanyCode}</td>
                  <td className="num">{nf.format(r.dayCount)}</td>
                  <td>
                    <button type="button" onClick={() => setOpen(r.CalendarID)}>
                      {mayWrite ? 'จัดการวันหยุด' : 'ดูวันหยุด'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length === 0 ? <p>ไม่มีข้อมูล</p> : null}
        </div>
      ) : null}

      {rows ? (
        <Pager
          page={page}
          pageSize={pageSize}
          total={total}
          loading={loading}
          unit="ปฏิทิน"
          onPage={setPage}
          onPageSize={(size) => {
            setPageSize(size)
            setPage(1)
          }}
        />
      ) : null}

      {open !== null ? (
        <CalendarDrawer
          calendarId={open === 'new' ? null : open}
          calendar={open === 'new' ? null : (rows?.find((r) => r.CalendarID === open) ?? null)}
          companies={companies}
          mayWrite={mayWrite}
          onClose={() => setOpen(null)}
          onSaved={() => void load()}
        />
      ) : null}
    </>
  )
}

/* ────────────────────────────────────────────────────────────────────────────
   แผงปฏิทินหนึ่งใบ — แก้หัวปฏิทิน และจัดการวันหยุดข้างใน
   ──────────────────────────────────────────────────────────────────────────── */

/* ถังขยะ — 16px เท่าความสูงบรรทัด วาดเองแทนลง icon lib เพราะทั้งระบบใช้ไอคอนอยู่ 2 ตัว
   stroke ไม่ใช่ fill ให้ดูเบาเท่าตัวหนังสือรอบข้าง ไม่ใช่ปุ่มที่ตะโกน */
function TrashIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M4 7h16M10 11v6M14 11v6M5 7l1 13h12l1-13M9 7V4h6v3" strokeLinecap="round" />
    </svg>
  )
}

function CalendarDrawer({
  calendarId,
  calendar,
  companies,
  mayWrite,
  onClose,
  onSaved,
}: {
  calendarId: number | null
  calendar: Calendar | null
  companies: { code: string; name: string | null }[]
  mayWrite: boolean
  onClose: () => void
  onSaved: () => void
}) {
  const creating = calendarId === null

  const [year, setYear] = useState(String(calendar?.CalendarYear ?? new Date().getFullYear()))
  const [remark, setRemark] = useState(calendar?.CalendarRemark ?? '')
  const [company, setCompany] = useState(calendar?.CompanyCode ?? companies[0]?.code ?? '')

  const [days, setDays] = useState<Day[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const [newDate, setNewDate] = useState('')
  const [newDesc, setNewDesc] = useState('')

  const loadDays = useCallback(async () => {
    if (calendarId === null) return
    try {
      const d = await api<{ rows: Day[] }>(`${BASE}/${calendarId}/days`)
      setDays(d.rows)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }, [calendarId])

  useEffect(() => {
    void loadDays()
  }, [loadDays])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true)
    try {
      await fn()
      setError(null)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  async function saveCalendar(e: React.FormEvent) {
    e.preventDefault()
    await run(async () => {
      await api(creating ? BASE : `${BASE}/${calendarId}`, {
        method: creating ? 'POST' : 'PUT',
        body: JSON.stringify({
          calendarYear: Number(year),
          calendarRemark: remark,
          companyCode: company,
        }),
      })
      onSaved()
      if (creating) onClose()
    })
  }

  async function addDay(e: React.FormEvent) {
    e.preventDefault()
    await run(async () => {
      await api(`${BASE}/${calendarId}/days`, {
        method: 'POST',
        body: JSON.stringify({ holidayDate: newDate, holidayDesc: newDesc }),
      })
      setNewDate('')
      setNewDesc('')
      await loadDays()
      onSaved()
    })
  }

  async function removeDay(d: Day) {
    if (!confirm(`ลบวันหยุด ${asDate(d.HolidayDate)} ${d.HolidayDesc}?`)) return
    await run(async () => {
      await api(`${BASE}/${calendarId}/days/${d.DateID}`, { method: 'DELETE' })
      await loadDays()
      onSaved()
    })
  }

  return (
    <>
      <button type="button" className="drawer-scrim" onClick={onClose} aria-label="ปิดแผง" />

      <aside className="drawer" role="dialog" aria-modal="true" aria-label="ปฏิทินวันหยุด">
        <header className="drawer-head">
          <div>
            <p className="drawer-title">{creating ? 'เพิ่มปฏิทิน' : `ปฏิทิน ${calendar?.CalendarYear ?? ''}`}</p>
            <p className="drawer-sub">{creating ? 'วันหยุดเพิ่มได้หลังบันทึก' : (calendar?.CalendarRemark ?? '')}</p>
          </div>
          <button type="button" className="drawer-close" onClick={onClose} aria-label="ปิด">
            ✕
          </button>
        </header>

        <div className="drawer-body">
          {error ? <p className="error">{error}</p> : null}

          <form className="emp-form" onSubmit={(e) => void saveCalendar(e)}>
            {/* ปี/บริษัทกว้างคงที่ ชื่อปฏิทินกินที่เหลือ — auto-fill จะให้ทั้งสามช่อง
                กว้างเท่ากัน แล้วชื่อปฏิทิน (ยาวสุด) แคบเท่าช่องปี ซึ่งอ่านไม่ออก */}
            <div className="field-grid" style={{ gridTemplateColumns: '7rem 9rem minmax(0, 1fr)' }}>
              <label>
                ปี (ค.ศ.)
                <input
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  inputMode="numeric"
                  required
                  disabled={!mayWrite}
                />
              </label>
              <label>
                บริษัท
                <select
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  required
                  disabled={!mayWrite || !creating}
                  title={!creating ? 'ย้ายปฏิทินข้ามบริษัทไม่ได้' : undefined}
                >
                  {companies.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.code}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                ชื่อปฏิทิน
                <input
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                  maxLength={600}
                  required
                  disabled={!mayWrite}
                />
              </label>
            </div>

            {mayWrite ? (
              <p>
                <button type="submit" disabled={busy}>
                  {busy ? 'กำลังบันทึก…' : creating ? 'สร้างปฏิทิน' : 'บันทึกหัวปฏิทิน'}
                </button>
              </p>
            ) : null}
          </form>

          {!creating ? (
            <>
              <p className="fieldset-title">วันหยุด {days ? `(${days.length} วัน)` : ''}</p>

              {mayWrite ? (
                <form className="emp-form" onSubmit={(e) => void addDay(e)}>
                  <div className="field-grid" style={{ gridTemplateColumns: '11rem minmax(0, 1fr)' }}>
                    <label>
                      วันที่
                      <DatePicker value={newDate} onChange={setNewDate} allowEmpty />
                    </label>
                    <label>
                      ชื่อวันหยุด
                      <input
                        value={newDesc}
                        onChange={(e) => setNewDesc(e.target.value)}
                        maxLength={600}
                        placeholder="เช่น วันสงกรานต์"
                      />
                    </label>
                  </div>
                  <p>
                    <button type="submit" disabled={busy || !newDate || !newDesc.trim()}>
                      เพิ่มวันหยุด
                    </button>
                  </p>
                </form>
              ) : null}

              {days === null ? (
                <p>กำลังโหลด…</p>
              ) : days.length === 0 ? (
                <p>ยังไม่มีวันหยุดในปฏิทินนี้</p>
              ) : (
                <div className="table-wrap table-wrap--rows10">
                  <table className="data">
                    <thead>
                      <tr>
                        <th>วันที่</th>
                        <th>ชื่อวันหยุด</th>
                        {mayWrite ? <th /> : null}
                      </tr>
                    </thead>
                    <tbody>
                      {days.map((d) => (
                        <tr key={d.DateID}>
                          <td className="nowrap">{asDate(d.HolidayDate)}</td>
                          <td>{d.HolidayDesc}</td>
                          {mayWrite ? (
                            <td>
                              {/* ไอคอนล้วน — ไม่มีข้อความให้ screen reader อ่าน ต้องมี aria-label
                                  และบอกด้วยว่าลบวันไหน ไม่งั้นได้ยิน "ลบ" ซ้ำกัน 16 ปุ่ม */}
                              <button
                                type="button"
                                className="ghost tiny icon-only"
                                onClick={() => void removeDay(d)}
                                disabled={busy}
                                aria-label={`ลบ ${d.HolidayDesc}`}
                                title="ลบ"
                              >
                                <TrashIcon />
                              </button>
                            </td>
                          ) : null}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          ) : null}
        </div>
      </aside>
    </>
  )
}
